import {Injectable} from '@angular/core';

import {BehaviorSubject, EMPTY, forkJoin, Observable} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

import * as moment from 'moment/moment';

import {EmpContractAttributes} from '../../models/models.interfaces';
import {ApiService} from '../api';
import {ProfileType} from '../employees/contract/contract.interfaces';
import {Contract, Employee, EmployeeDetail, mapContract} from '../employees/employees.model';
import {DataTransformer} from '../form-generator/data-transformer/data-transformer';
import {FormDescriptorService} from '../form-generator/form-descriptor.service';
import {ZoneValues} from '../form-generator/form-generator.interfaces';
import {FormGeneratorService} from '../form-generator/form-generator.service';
import {User} from '../user';
import {ProcessInstance} from '../workflow/workflow-v2.interface';
import {getWorkflowKey, WorkflowV2Service} from '../workflow/workflow-v2.service';
import {ProfileMode} from '../../app.constants';


@Injectable()
export class ContractDataService {
  public employee: Employee;
  public selectedContract: Contract = [];

  public userSubject: BehaviorSubject<User> = new BehaviorSubject<User>(null);
  public currentUser = this.userSubject.asObservable();

  private cachedContractByCliEmp = {};

  constructor(private api: ApiService,
              private formGeneratorService: FormGeneratorService,
              private workflowV2Service: WorkflowV2Service,
              private formDescriptorService: FormDescriptorService) {
  }

  public getContractDataAndDescriptor(contractId: number, cliRegId: number, profileType: ProfileType): Observable<any> {

    return forkJoin(
      this.formDescriptorService.getFormDescriptorContractID(contractId, cliRegId, profileType),
      this.getContractData(contractId, profileType)
    )
      .pipe(
        map(([descriptor, contract]: Array<any>) => {
          Object.assign(this.selectedContract, contract);
          const transformer = new DataTransformer(descriptor, 'empDatas', this.formGeneratorService);
          return {
            descriptor,
            contractData: transformer.transform(this.selectedContract)
          };
        })
      );
  }

  public updatePersonalInfo(values: { [key: string]: ZoneValues[] }, contractId: number) {
    return this.api.post(`/ga/api/v1/workflow/changeInfo/${contractId}`, values);
  }

  public createUpdatePersonalInfoWorkflowV2(requestVariables: any, workflowName: string, cliRegId: number, profileMode: ProfileMode): Observable<ProcessInstance> {
    const workflowKey = getWorkflowKey(workflowName, cliRegId);
    return this.workflowV2Service.startProcessInstance(workflowKey, {
      request: requestVariables,
      groupName: requestVariables['groupName'],
      groupZoneTransId: requestVariables.groupZoneTransId,
      cliRegId
    }, false, profileMode);
  }

  public getMainContract(contracts: Contract[]): Contract {
    const now: Date = moment().startOf('day').toDate();

    return contracts.find((contract) => contract.mainContract)
      || contracts.find((contract) => moment(contract.enddate).isAfter(now))
      || contracts[0];
  }

  private getContractData(contractId: number, profileType?: ProfileType): Observable<EmpContractAttributes> {
    return this.api.get(`/ga/api/v1/empContract/${contractId}?` +
      `isEss=${profileType === ProfileType.ESS}&isMss=${profileType === ProfileType.MSS}`)
      .pipe(map(result => result.data));
  }

  private getAPIContractsByEmployeeId(id: number): Observable<Contract[]> {
    return this.api.get(`/ga/api/v1/cliEmp?search=id==${id}&isEss=true`)
      .pipe(
        map(result => {
          const data = <EmployeeDetail>result.data[0];
          if (!data) {
            return null;
          }
          const employee = new Employee(data);
          return employee.contracts.map(mapContract);
        }),
        catchError(() => EMPTY)
      );
  }

  public getContractsByEmployeeId(id: number): Observable<Contract[]> {
    if (!this.cachedContractByCliEmp[id]) {
      this.cachedContractByCliEmp[id] = this.getAPIContractsByEmployeeId(id);
    }
    return this.cachedContractByCliEmp[id];
  }
}
